import { Dispatch, MutableRefObject, SetStateAction, useCallback, useRef, useState } from "react";

/**
 * A custom hook that behaves like `useState`, but also provides a mutable ref
 * that always holds the latest state value.
 *
 * This is useful for accessing the most up-to-date state in asynchronous
 * callbacks or event listeners where closures might otherwise capture stale values.
 *
 * @template T - The type of the state value.
 *
 * @param {T | (() => T)} initialState - The initial state value or a function that returns the initial state.
 *
 * @returns {[T, Dispatch<SetStateAction<T>>, MutableRefObject<T>]} A tuple containing:
 * - The current state value.
 * - A setter function that updates the state and the ref.
 * - A mutable ref object that always points to the latest state value.
 *
 * @example
 * const [count, setCount, countRef] = useStateRef(0);
 *
 * // Access latest value in async function
 * useEffect(() => {
 *   const timer = setInterval(() => {
 *     console.log(countRef.current); // always latest
 *   }, 1000);
 *   return () => clearInterval(timer);
 * }, []);
 */
export function useStateRef<T>(initialState: T | (() => T)): [T, Dispatch<SetStateAction<T>>, MutableRefObject<T>] {
    const [state, setState] = useState<T>(initialState);
    const stateRef = useRef<T>(state);

    // update the stateRef whenever the state changes
    const setStateAndRef: Dispatch<SetStateAction<T>> = useCallback((value: SetStateAction<T>) => {
        // if the value is a function, call it with the previous state
        // otherwise, use the value directly
        const newValue = typeof value === 'function' ? (value as (prevState: T) => T)(stateRef.current) : value;

        // update stateRef
        stateRef.current = newValue;

        // update the state using the new value
        setState(newValue);
    }, []);

    // return the state and the setter function
    return [state, setStateAndRef, stateRef] as const;
}

export default useStateRef;